import {
  Card,
  ChevronLeftIcon,
  ContentFooterActions,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonSection,
  IconSizeEnum,
  SpinnerIcon,
  Typography,
  UploadAudienceIcon,
} from '@acceso/design-system';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InputDate } from '@/components/FormFields/InputDate';
import { InputLogos } from '@/components/FormFields/InputLogos';
import { RowContainer } from '@/components/FormFields/RowContainer';
import { SelectForm } from '@/components/FormFields/SelectForm';
import { useChannelScopeOptions } from '@/features/broadcasters/hooks/useChannelScopeOptions';
import { ChannelScopeEnum } from '@/features/broadcasters/types';
import { useLocationOptions } from '@/features/locations/hooks/useLocationOptions';
import { routerManager } from '@/routes/app-routes';
import { AVRouteEnum } from '@/routes/constants/AVRouteEnum';

import { usePatchAudience } from '../api/patch-audiences-file';
import {
  AUDIENCE_FILE_MAX_SIZE,
  AUDIENCE_PROVIDER_CONFIG,
  AUDIENCE_PROVIDER_OPTIONS,
} from '../constants';
import { useAudiencePermissions } from '../hooks/useAudiencePermissions';
import { TAudienceForm } from '../types';
// import { useAudiences } from '../api/get-audience';

export const AudiencePage = () => {
  const { t } = useTranslation();
  const form = useForm<TAudienceForm>({
    defaultValues: {
      provider: undefined,
      scope: undefined,
      country_code: '',
      region_code: '',
      subregion_code: '',
      start_datetime: undefined,
    },
  });
  const { country_code: countryCode } = useWatch({ control: form.control });
  const provider = useWatch({ control: form.control, name: 'provider' });
  const selectedScope = useWatch({ control: form.control, name: 'scope' });
  const selectedRegionCode = useWatch({ control: form.control, name: 'region_code' });

  const isSubregional = selectedScope === ChannelScopeEnum.SUBREGIONAL;
  const providerConfig = provider ? AUDIENCE_PROVIDER_CONFIG[provider] : undefined;

  const { countryOptions, regionOptions, subregionOptions } = useLocationOptions({
    countryCode,
    regionCode: isSubregional ? selectedRegionCode : undefined,
  });
  const scope = useChannelScopeOptions();

  useEffect(() => {
    form.setValue('subregion_code', '');
  }, [selectedScope, selectedRegionCode]);

  // Providers that declare defaults get them applied on selection. They are a
  // convenience and not a constraint: every field keeps its full option list.
  // Region and subregion are cleared because a value carried over from another
  // provider's regional selection would otherwise ride along in the payload.
  useEffect(() => {
    const defaults = providerConfig?.defaults;

    if (!defaults) {
      return;
    }

    form.setValue('scope', defaults.scope);
    form.setValue('country_code', defaults.countryCode);
    form.setValue('region_code', '');
    form.setValue('subregion_code', '');
  }, [provider]);
  const { mutate: patchAudience, isPending, uploadProgress } = usePatchAudience();
  // const { data: audiences = [] } = useAudiences();

  const handleSubmit = form.handleSubmit(data => {
    patchAudience(data);
  });

  const { canCreate } = useAudiencePermissions();

  return (
    <>
      <div className="flex items-center justify-between py-size-8">
        <Typography
          fontSize={FontSizeEnum.Xl}
          weight={FontWeightEnum.Bold}
          color={FontColorEnum.Dark10}
        >
          {t('Cargar fichero de audiencias')}
        </Typography>

        <IconButtonSection
          onClick={() => routerManager.to(AVRouteEnum.AUDIENCE_CHANNEL_SOURCES)}
          internalProps={{
            icon: {
              size: IconSizeEnum.SM,
              type: ChevronLeftIcon,
            },
            text: {
              value: t('Fuentes de audiencia'),
              color: FontColorEnum.Default,
            },
          }}
        />
      </div>
      <Card>
        <div className="flex items-center px-5">
          <div>
            {isPending ? (
              <div className="flex w-[260px] flex-col items-center">
                <SpinnerIcon size={IconSizeEnum.CUSTOM} className="mb-4 size-40" />
                <Typography
                  className="mb-4 text-justify"
                  fontSize={FontSizeEnum.Xl}
                  weight={FontWeightEnum.Bold}
                  color={FontColorEnum.Secondary}
                >
                  {t('Cargando...')}
                </Typography>
                <Typography
                  className="mb-2 text-center"
                  fontSize={FontSizeEnum.Sm}
                  weight={FontWeightEnum.Regular}
                  color={FontColorEnum.Dark10}
                >
                  {t(
                    'El proceso puede tardar unos minutos, no cierre esta página hasta que el archivo se cargue completamente.'
                  )}
                </Typography>
                <Typography
                  className="text-center"
                  fontSize={FontSizeEnum.Sm}
                  weight={FontWeightEnum.Regular}
                  color={FontColorEnum.Dark10}
                >
                  {t('Puede seguir trabajando abriendo otra página del navegador.')}
                </Typography>
                <Typography
                  className="mt-4 text-center"
                  fontSize={FontSizeEnum.Lg}
                  weight={FontWeightEnum.Regular}
                  color={FontColorEnum.Dark10}
                >
                  {uploadProgress ? `${uploadProgress}%` : ''}
                </Typography>
              </div>
            ) : (
              <div className="flex flex-col items-center p-[120px]">
                <UploadAudienceIcon size={IconSizeEnum.CUSTOM} className="mb-4" />
                <Typography
                  fontSize={FontSizeEnum.Xl}
                  weight={FontWeightEnum.Bold}
                  color={FontColorEnum.Dark10}
                  className="whitespace-nowrap"
                >
                  {t('Cargar un nuevo archivo')}
                </Typography>
              </div>
            )}
          </div>
          <div className="flex w-full flex-col gap-4">
            <RowContainer>
              <SelectForm
                options={AUDIENCE_PROVIDER_OPTIONS}
                form={form}
                name="provider"
                required
                label={t('Proveedor')}
                placeholder={t('Seleccionar...')}
              />
              <SelectForm
                options={scope}
                form={form}
                required
                name="scope"
                label={t('Ámbito')}
                placeholder={t('Seleccionar...')}
              />
            </RowContainer>
            <RowContainer>
              <SelectForm
                options={countryOptions}
                form={form}
                required
                name="country_code"
                label={t('País')}
                placeholder={t('Seleccionar...')}
              />
              <SelectForm
                options={regionOptions}
                form={form}
                name="region_code"
                label={t('Región')}
                placeholder={t('Seleccionar...')}
              />
            </RowContainer>
            {isSubregional && (
              <RowContainer>
                <SelectForm
                  options={subregionOptions}
                  form={form}
                  required
                  name="subregion_code"
                  label={t('Subregión')}
                  placeholder={t('Seleccionar...')}
                />
              </RowContainer>
            )}
            {providerConfig?.requiresPeriodStart && (
              <RowContainer>
                <InputDate
                  form={form}
                  name="start_datetime"
                  label={t('Fecha de inicio de periodo')}
                  showTimeField={false}
                />
              </RowContainer>
            )}

            <InputLogos
              singleFile
              form={form}
              name="file"
              newFiles
              required
              label={t('Seleccionar archivos')}
              accept={providerConfig?.fileAccept}
              format={providerConfig?.fileFormatLabel}
              maxSize={providerConfig?.maxFileSize ?? AUDIENCE_FILE_MAX_SIZE}
            />
          </div>
        </div>
        {canCreate && (
          <ContentFooterActions
            onClose={() => form.reset()}
            actionLabel={t('Cargar')}
            linkLabel={t('Borrar')}
            onAction={() => handleSubmit()}
            disabled={isPending}
          />
        )}
      </Card>
    </>
  );
};
